import {
  Children,
  forwardRef,
  useMemo,
  type ReactElement,
  type ReactNode,
  type Ref,
} from 'react';

import {
  type DataTestId,
  type StylingProps,
  type WithChildren,
} from '@dynatrace/strato-components/core';
import { Flex } from '@dynatrace/strato-components/layouts';

import { DataTableV2ColumnOrderSettings } from './ColumnSettings/DataTableV2ColumnOrderSettings.js';
import { DataTableV2ColumnSettings } from './ColumnSettings/DataTableV2ColumnSettings.js';
import { DataTableV2ColumnSettingsTrigger } from './ColumnSettings/DataTableV2ColumnSettingsTrigger.js';
import { DataTableV2ColumnVisibilitySettings } from './ColumnSettings/DataTableV2ColumnVisibilitySettings.js';
import { DataTableV2ToolbarMenu } from './DataTableV2ToolbarMenu.js';
import {
  Intent,
  type IntentProps,
} from '../../../../core/slots/Intent/Intent.js';
import { isSlot } from '../../../../core/utils/is-slot.js';
import { DataTableV2ColumnVisibilityState } from '../../features/ColumnVisibility/ColumnVisibility.js';
import { DataTableV2Download } from '../../features/Download/Download.js';
import {
  DataTableV2GlobalLineWrapAction,
  DataTableV2GlobalLineWrapToolbarAction,
} from '../../features/LineWrap.js';

/**
 * @public
 */
export type DataTableV2ToolbarProps = WithChildren & StylingProps & DataTestId;

// A helper function to check whether the child component is the TableActions component
/**
 * @internal
 */
export function _isDataTableV2Toolbar(
  child: ReactElement | null,
): child is ReactElement<DataTableV2ToolbarProps> {
  return child?.type === DataTableV2Toolbar;
}

/**
 * Allowed slots for the DataTableToolbar.
 */
type DataTableV2ToolbarSlots = {
  visibilitySettings: boolean;
  columnOrderSettings: boolean;
  downloadData: boolean;
  globalLineWrap: boolean;
};

/**
 * Props for the DataTableToolbar items.
 */
type DataTableV2ToolbarItemProps = {
  resetColumnVisibility?: DataTableV2ColumnVisibilityState;
  resetColumnOrder?: string[];
  customTrigger?: (props: {
    hiddenColumnCount: number;
    totalColumnCount: number;
    defaultLabel: string;
  }) => string;
  intents: Array<IntentProps>;
};

/**
 * @internal
 */
export function getToolbarConfig(children: ReactNode) {
  const slots: DataTableV2ToolbarSlots = {
    visibilitySettings: false,
    columnOrderSettings: false,
    downloadData: false,
    globalLineWrap: false,
  };
  const toolbarItemProps: DataTableV2ToolbarItemProps = { intents: [] };

  Children.forEach(children, (child) => {
    if (isSlot(child, DataTableV2ColumnVisibilitySettings)) {
      slots.visibilitySettings = true;
      toolbarItemProps.resetColumnVisibility =
        child.props?.resetColumnVisibility;
    } else if (isSlot(child, DataTableV2ColumnOrderSettings)) {
      slots.columnOrderSettings = true;
      toolbarItemProps.resetColumnOrder = child.props?.resetColumnOrder;
    } else if (isSlot(child, DataTableV2ColumnSettingsTrigger)) {
      toolbarItemProps.customTrigger = child.props?.children;
    } else if (isSlot(child, DataTableV2Download)) {
      slots.downloadData = true;
    } else if (isSlot(child, DataTableV2GlobalLineWrapAction)) {
      slots.globalLineWrap = true;
    } else if (isSlot(child, Intent)) {
      toolbarItemProps.intents.push(child.props);
    } else {
      // eslint-disable-next-line no-console
      console.warn('Invalid child of DataTableV2Toolbar');
    }
  });

  return { slots, toolbarItemProps };
}

/**
 * Toolbar slot component for general actions that affect the entire table. Positioned above the header.
 * @public
 */
export const DataTableV2Toolbar = forwardRef(
  (props: DataTableV2ToolbarProps, forwardedRef: Ref<HTMLDivElement>) => {
    const {
      children,
      'data-testid': dataTestId,
      className: consumerClassName,
      style: consumerStyle,
      ...remainingProps
    } = props;

    const { slots, toolbarItemProps } = useMemo(
      () => getToolbarConfig(children),
      [children],
    );

    if (Children.count(children) === 0) {
      return null;
    }

    return (
      <Flex
        ref={forwardedRef}
        justifyContent="flex-end"
        alignItems="flex-end"
        gap={4}
        data-testid={dataTestId}
        className={consumerClassName}
        style={{
          gridArea: '1 / 2 / 2 / 2',
          ...consumerStyle,
        }}
        {...remainingProps}
      >
        {(slots.visibilitySettings || slots.columnOrderSettings) && (
          <DataTableV2ColumnSettings
            customTrigger={toolbarItemProps?.customTrigger}
          />
        )}
        {slots.globalLineWrap && <DataTableV2GlobalLineWrapToolbarAction />}
        {(slots.downloadData || toolbarItemProps.intents.length > 0) && (
          <DataTableV2ToolbarMenu
            hasDownloadData={slots.downloadData}
            intents={toolbarItemProps.intents}
          />
        )}
      </Flex>
    );
  },
);

(
  DataTableV2Toolbar as typeof DataTableV2Toolbar & {
    displayName: string;
  }
).displayName = 'DataTableV2.Toolbar';
